import {
	type BoundActions,
	createHook,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { type StreamMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import type { MessageResearch } from './MessageResearch';
import { store } from './store';
import type { ChatMessageResearchStore } from './types';

export const useIsDeepResearchInProgress: HookFunction<
	boolean,
	BoundActions<
		ChatMessageResearchStore,
		{
			resetHasReceivedAnswerPart: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
			addImplicitResearchPlan: ({
				messageId,
				content,
				roundId,
			}: {
				messageId: string;
				content: string;
				roundId: number;
			}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
			processStreamResearch: ({
				messageId,
				streamMessage,
				isDeepResearch,
			}: {
				messageId: string;
				streamMessage: StreamMessage;
				isDeepResearch?: boolean;
			}) => ({ dispatch, getState }: StoreActionApi<ChatMessageResearchStore>) => void;
			updateResearchMessageId: ({
				messageId,
				newMessageId,
			}: {
				messageId: string;
				newMessageId: string;
			}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
			incrementProgressArtificially: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
			markIncompleteResearchesErrored: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
			completeAllSteps: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ dispatch }: StoreActionApi<ChatMessageResearchStore>) => void;
			getMessageResearchById: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ getState }: StoreActionApi<ChatMessageResearchStore>) => MessageResearch | undefined;
			/**
			 * Clears all research data for a specific message.
			 * Used when Answer Now is triggered to reset the research state.
			 */
			clearMessageResearch: ({
				messageId,
			}: {
				messageId: string;
			}) => ({ getState, setState }: StoreActionApi<ChatMessageResearchStore>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => {
		return Object.values(state).some((messageResearch) => {
			if (!messageResearch) {
				return false;
			}
			const isInProgress = !!(
				messageResearch.currentProgress && messageResearch.currentProgress > 0
			);
			const isErrored = !!messageResearch.errored;
			const isCompleted = !!messageResearch.researchCompleted;
			const isCompletedOrErrored = isCompleted || isErrored;
			const isDeepResearch = messageResearch.isDeepResearch;

			return isInProgress && !isCompletedOrErrored && isDeepResearch;
		});
	},
});
